import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    CircularProgress,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Typography
} from '@mui/material';
import {firstRowsOfCsv} from '../pages/SchoolDashboard/Reports/CreateReport/createReportRules';

// Maria, 17 October 2026: spreadsheets are shown in the preview pop-up as a table, in the same style as the report
// preview in the Create Report box. Each sheet has a tab. Very long sheets show their first rows; 'Download' gives
// the whole file.
export const ROW_LIMIT = 1000;

export const isSpreadsheet = (type = '', name = '') =>
    /spreadsheetml|text\/csv|application\/csv/i.test(type) || /\.(xlsx|csv)$/i.test(name);

const isCsv = (type = '', name = '') => /csv/i.test(type) || /\.csv$/i.test(name);

const cellText = cell => {
    if (cell == null) return '';
    if (typeof cell === 'object') {
        if (cell.text) return String(cell.text);
        if (cell.richText) return cell.richText.map(part => part.text).join('');
        if (cell.result != null) return String(cell.result);
        if (cell instanceof Date) return cell.toLocaleDateString('en-GB');
        if (cell.hyperlink) return String(cell.text ?? cell.hyperlink);
    }

    return String(cell);
};

// The sheets of a spreadsheet as {name, header, rows, count}.
export const readSheets = async (blob, name) => {
    if (isCsv(blob.type, name)) {
        const {header, rows, count} = firstRowsOfCsv(await blob.text(), ROW_LIMIT);
        return [{name: 'Sheet 1', header, rows, count}];
    }

    const {default: ExcelJS} = await import('exceljs');
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(await blob.arrayBuffer());

    return workbook.worksheets.map(sheet => {
        const all = [];
        sheet.eachRow({includeEmpty: false}, row => {
            all.push(Array.from({length: sheet.columnCount}, (_, i) => cellText(row.getCell(i + 1).value)));
        });
        const [header = [], ...rows] = all;

        return {name: sheet.name, header, rows: rows.slice(0, ROW_LIMIT), count: rows.length};
    });
};

const SpreadsheetPreview = ({blob, name, onCannotShow}) => {
    const [sheets, setSheets] = useState(null);
    const [current, setCurrent] = useState(0);

    useEffect(() => {
        let cancelled = false;
        readSheets(blob, name)
            .then(result => !cancelled && setSheets(result))
            .catch(() => !cancelled && onCannotShow());

        return () => {
            cancelled = true;
        };
    }, [blob, name]);

    if (!sheets) {
        return <CircularProgress size={36}/>;
    }

    const sheet = sheets[current] ?? {header: [], rows: [], count: 0};

    return (
        <Box sx={{width: '100%', height: '100%', display: 'flex', flexDirection: 'column', p: 2, gap: 1}} data-testid="spreadsheet-preview">
            {sheets.length > 1 && (
                <Tabs value={current} onChange={(_, value) => setCurrent(value)} variant="scrollable" sx={{minHeight: 0}}>
                    {sheets.map((item, index) => <Tab key={item.name} value={index} label={item.name} sx={{textTransform: 'none', minHeight: 0, py: 1}}/>)}
                </Tabs>
            )}
            {sheet.count > ROW_LIMIT && (
                <Typography variant="body2">
                    Showing the first {ROW_LIMIT} of {sheet.count} rows. Click on &apos;Download&apos; for the whole file.
                </Typography>
            )}
            <TableContainer sx={{flex: 1, minHeight: 0, border: '1px solid #e3e8ef', borderRadius: 1, backgroundColor: '#fff'}}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {sheet.header.map((cell, index) => (
                                <TableCell key={index} sx={{fontWeight: 600, whiteSpace: 'nowrap', backgroundColor: '#f5f7fa'}}>{cell}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sheet.rows.map((row, rowIndex) => (
                            <TableRow key={rowIndex}>
                                {row.map((cell, cellIndex) => (
                                    <TableCell key={cellIndex} sx={{whiteSpace: 'nowrap'}}>{cell}</TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

SpreadsheetPreview.propTypes = {
    blob: PropTypes.instanceOf(Blob).isRequired,
    name: PropTypes.string,
    onCannotShow: PropTypes.func.isRequired
};

export default SpreadsheetPreview;
